<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 sm:p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              📊 Dashboard de Cotizaciones
            </h1>
            <p class="text-gray-600">
              Panel de control con gráficas simples y contenidas
            </p>
          </div>
          
          <!-- Selector de fecha -->
          <div class="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
            <label class="text-sm font-medium text-gray-700">Período:</label>
            <select 
              v-model="selectedYear" 
              @change="loadData" 
              class="bg-white border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="2024">2024</option>
              <option value="2025">2025</option>
            </select>
            <select 
              v-model="selectedMonth" 
              @change="loadData" 
              class="bg-white border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option v-for="(month, index) in months" :key="index" :value="index + 1">
                {{ month }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="bg-white rounded-lg shadow p-8 text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p class="text-gray-600">Cargando datos del dashboard...</p>
        </div>
      </div>

      <!-- Dashboard Content -->
      <div v-else-if="!error" class="space-y-6">
        <!-- KPIs Summary -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <!-- Total Cotizaciones -->
          <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-blue-100 text-sm font-medium">Total Cotizaciones</p>
                <p class="text-2xl font-bold">{{ formatNumber(dashboardData?.resumen_general?.total_cotizaciones_ano || 0) }}</p>
              </div>
              <div class="text-blue-200">
                <i class="las la-file-alt text-3xl"></i>
              </div>
            </div>
          </div>

          <!-- Ventas del Año -->
          <div class="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-green-100 text-sm font-medium">Total Ventas Año</p>
                <p class="text-2xl font-bold">{{ formatNumber(dashboardData?.resumen_general?.total_ventas_ano || 0) }}</p>
              </div>
              <div class="text-green-200">
                <i class="las la-shopping-cart text-3xl"></i>
              </div>
            </div>
          </div>

          <!-- Monto Total -->
          <div class="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-purple-100 text-sm font-medium">Monto Total</p>
                <p class="text-xl font-bold">${{ formatCurrency(dashboardData?.resumen_general?.monto_total_ventas_ano || 0) }}</p>
              </div>
              <div class="text-purple-200">
                <i class="las la-dollar-sign text-3xl"></i>
              </div>
            </div>
          </div>

          <!-- Tasa de Conversión -->
          <div class="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-yellow-100 text-sm font-medium">Conversión</p>
                <p class="text-2xl font-bold">{{ formatPercentage(dashboardData?.resumen_general?.conversion_rate || 0) }}%</p>
              </div>
              <div class="text-yellow-200">
                <i class="las la-chart-line text-3xl"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Gráficas Simples con CSS -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Cotizaciones por Mes -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">📈 Cotizaciones por Mes</h3>
            <div class="h-64 w-full border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center relative overflow-hidden">
              <!-- Barras dinámicas con datos reales -->
              <div class="flex items-end space-x-1 h-48 w-full px-4">
                <div 
                  v-for="(mes, index) in getChartDataCotizaciones" 
                  :key="index"
                  class="bg-blue-500 rounded-t transition-all duration-500 relative group cursor-pointer"
                  :style="`height: ${mes.height}%; width: ${100/12}%; max-width: 8%`"
                  :title="`${mes.mes}: ${mes.cantidad} cotizaciones`"
                >
                  <!-- Tooltip hover -->
                  <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {{ mes.cantidad }}
                  </div>
                </div>
              </div>
              <!-- Etiquetas de meses -->
              <div class="absolute bottom-2 left-0 right-0 flex justify-around text-xs text-gray-500">
                <span v-for="mes in ['E','F','M','A','M','J','J','A','S','O','N','D']" :key="mes">{{ mes }}</span>
              </div>
            </div>
          </div>
          
          <!-- Ventas por Mes -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">💰 Ventas por Mes</h3>
            <div class="h-64 w-full border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center relative overflow-hidden">
              <!-- Barras dinámicas con datos reales -->
              <div class="flex items-end space-x-1 h-48 w-full px-4">
                <div 
                  v-for="(mes, index) in getChartDataVentas" 
                  :key="index"
                  class="bg-green-500 rounded-t transition-all duration-500 relative group cursor-pointer"
                  :style="`height: ${mes.height}%; width: ${100/12}%; max-width: 8%`"
                  :title="`${mes.mes}: ${mes.cantidad} ventas`"
                >
                  <!-- Tooltip hover -->
                  <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {{ mes.cantidad }}
                  </div>
                </div>
              </div>
              <!-- Etiquetas de meses -->
              <div class="absolute bottom-2 left-0 right-0 flex justify-around text-xs text-gray-500">
                <span v-for="mes in ['E','F','M','A','M','J','J','A','S','O','N','D']" :key="mes">{{ mes }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Sección de Tablas y Rankings -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Top Vendedores -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">🏆 Top Vendedores</h3>
            <div class="space-y-3">
              <div v-for="(vendedor, index) in getTopVendedores" :key="vendedor.id" 
                   class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {{ index + 1 }}
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ vendedor.name }}</p>
                    <p class="text-sm text-gray-500">{{ vendedor.total_ventas }} ventas ({{ formatPercentage(vendedor.conversion_rate) }}%)</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="font-bold text-green-600">${{ formatCurrency(vendedor.monto_ventas) }}</p>
                  <p class="text-xs text-gray-500">{{ vendedor.total_cotizaciones }} cotizaciones</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Productos Más Vendidos -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">🛍️ Productos Más Vendidos</h3>
            <div class="space-y-3">
              <div v-for="(producto, index) in getProductosMasVendidos" :key="producto.id" 
                   class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {{ index + 1 }}
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ producto.nombre }}</p>
                    <p class="text-sm text-gray-500">{{ producto.total_cotizaciones }} cotizaciones</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="font-bold text-purple-600">{{ producto.cantidad_vendida }} u.</p>
                  <p class="text-xs text-gray-500">${{ formatCurrency(producto.monto_ventas) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Métricas Adicionales -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Ticket Promedio -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900">💳 Ticket Promedio</h3>
              <i class="las la-receipt text-2xl text-indigo-500"></i>
            </div>
            <p class="text-3xl font-bold text-indigo-600">
              ${{ formatCurrency(dashboardData?.resumen_general?.ticket_promedio || 0) }}
            </p>
            <p class="text-sm text-gray-500 mt-2">Promedio por venta</p>
          </div>

          <!-- Métricas del Mes Actual -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900">� Mes Actual</h3>
              <i class="las la-calendar text-2xl text-green-500"></i>
            </div>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">Cotizaciones:</span>
                <span class="font-semibold">{{ dashboardData?.metricas_mes_actual?.cotizaciones || 0 }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">Ventas:</span>
                <span class="font-semibold text-green-600">{{ dashboardData?.metricas_mes_actual?.ventas || 0 }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">Conversión:</span>
                <span class="font-semibold text-blue-600">{{ formatPercentage(dashboardData?.metricas_mes_actual?.conversion_rate || 0) }}%</span>
              </div>
            </div>
          </div>

          <!-- Estado de Producción -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900">� Producción</h3>
              <i class="las la-cogs text-2xl text-orange-500"></i>
            </div>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600">Total productos:</span>
                <span class="font-semibold">{{ dashboardData?.estado_produccion?.total_productos || 0 }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Terminados:</span>
                <span class="font-semibold text-green-600">{{ dashboardData?.estado_produccion?.productos_terminados || 0 }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Instalados:</span>
                <span class="font-semibold text-blue-600">{{ dashboardData?.estado_produccion?.productos_instalados || 0 }}</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2 mt-3">
                <div class="bg-orange-500 h-2 rounded-full transition-all duration-500" 
                     :style="`width: ${getProduccionProgress}%`"></div>
              </div>
              <p class="text-xs text-gray-500 text-center">{{ getProduccionProgress }}% completado</p>
            </div>
          </div>
        </div>

        <!-- Resumen de Productos Vendidos -->
        <div class="bg-white rounded-xl shadow-lg p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">📦 Resumen de Productos Vendidos</h3>
            <span class="text-sm text-gray-500">Total: {{ calcTotalProductos }} productos</span>
          </div>
          
          <div class="overflow-x-auto">
            <table class="min-w-full bg-white">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cotizaciones</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad Total</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendidas</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Ventas</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="producto in getProductosMasVendidos" :key="producto.id">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">{{ producto.nombre }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {{ producto.total_cotizaciones }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ producto.cantidad_total }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ producto.cantidad_vendida }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    ${{ formatCurrency(producto.monto_ventas) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="error && !loading" class="bg-white rounded-xl shadow-lg p-8 text-center">
        <div class="text-red-500 mb-4">
          <i class="las la-exclamation-triangle text-4xl"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Error al cargar datos</h3>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <button 
          @click="loadData" 
          class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Reintentar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from '@/stores/user'
import tablero from '@/Services/tablero'
import { emitter } from '@/mitt.js'

export default {
  name: 'DashboardCotizacionesSimple',
  data() {
    return {
      selectedYear: 2025,
      selectedMonth: 9,
      months: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      loading: false,
      error: null,
      dashboardData: null
    }
  },
  
  computed: {
    calcTotalProductos() {
      if (!this.dashboardData?.productos_mas_vendidos) return 0
      return this.dashboardData.productos_mas_vendidos.reduce((total, producto) => 
        total + (producto.cantidad_vendida || 0), 0
      )
    },
    
    getTopVendedores() {
      return this.dashboardData?.top_vendedores || []
    },
    
    getProductosMasVendidos() {
      return this.dashboardData?.productos_mas_vendidos || []
    },
    
    getChartDataCotizaciones() {
      const meses = this.dashboardData?.cotizaciones_por_mes || []
      const maxValue = Math.max(...meses.map(m => m.cantidad || 0), 1)
      
      // Crear array de 12 meses con datos
      const chartData = []
      for (let i = 1; i <= 12; i++) {
        const mesData = meses.find(m => m.key === i)
        const cantidad = mesData ? mesData.cantidad : 0
        const height = maxValue > 0 ? (cantidad / maxValue) * 100 : 0
        
        chartData.push({
          mes: mesData ? mesData.mes : '',
          cantidad: cantidad,
          height: Math.max(height, cantidad > 0 ? 5 : 0) // Mínimo 5% si hay datos
        })
      }
      
      return chartData
    },
    
    getChartDataVentas() {
      const meses = this.dashboardData?.ventas_por_mes || []
      const maxValue = Math.max(...meses.map(m => m.cantidad || 0), 1)
      
      // Crear array de 12 meses con datos
      const chartData = []
      for (let i = 1; i <= 12; i++) {
        const mesData = meses.find(m => m.key === i)
        const cantidad = mesData ? mesData.cantidad : 0
        const height = maxValue > 0 ? (cantidad / maxValue) * 100 : 0
        
        chartData.push({
          mes: mesData ? mesData.mes : '',
          cantidad: cantidad,
          height: Math.max(height, cantidad > 0 ? 5 : 0) // Mínimo 5% si hay datos
        })
      }
      
      return chartData
    },
    
    getProduccionProgress() {
      const estado = this.dashboardData?.estado_produccion
      if (!estado || !estado.total_productos) return 0
      
      const instalados = estado.productos_instalados || 0
      const total = estado.total_productos
      
      return Math.round((instalados / total) * 100)
    }
  },
  
  mounted() {
    this.loadData()
  },
  
  methods: {
    async loadData() {
      this.loading = true
      this.error = null
      
      console.log(`🔄 Cargando datos para año: ${this.selectedYear}, mes: ${this.selectedMonth}`)
      
      try {
        emitter.emit("loading", true)
        
        const userStore = useUserStore()
        if (!userStore.token) {
          throw new Error('No hay token de usuario')
        }

        // Cargar todos los datos desde el endpoint principal
        const response = await tablero.obtenerDatosCotizaciones(this.selectedYear, this.selectedMonth)
        this.dashboardData = response.data || response
        
        console.log('✅ Datos completos cargados desde API:', this.dashboardData)
        
      } catch (error) {
        console.error('❌ Error loading dashboard data:', error)
        this.error = 'Error de conexión. Por favor, inténtelo de nuevo.'
        
        // Datos de fallback con la nueva estructura
        this.dashboardData = {
          resumen_general: {
            total_cotizaciones_ano: 245,
            total_ventas_ano: 127,
            monto_total_ventas_ano: 2847500.00,
            conversion_rate: 51.84,
            ticket_promedio: 22421.26
          },
          metricas_mes_actual: {
            cotizaciones: 35,
            ventas: 18,
            monto_ventas: 425000.00,
            conversion_rate: 51.43
          },
          cotizaciones_por_mes: [
            { mes: "ENERO", key: 1, cantidad: 18, monto: 320000.00 },
            { mes: "FEBRERO", key: 2, cantidad: 22, monto: 450000.00 },
            { mes: "MARZO", key: 3, cantidad: 25, monto: 380000.00 },
            { mes: "ABRIL", key: 4, cantidad: 30, monto: 520000.00 },
            { mes: "MAYO", key: 5, cantidad: 28, monto: 480000.00 },
            { mes: "JUNIO", key: 6, cantidad: 35, monto: 620000.00 },
            { mes: "JULIO", key: 7, cantidad: 32, monto: 580000.00 },
            { mes: "AGOSTO", key: 8, cantidad: 38, monto: 680000.00 },
            { mes: "SEPTIEMBRE", key: 9, cantidad: 35, monto: 625000.00 },
            { mes: "OCTUBRE", key: 10, cantidad: 40, monto: 720000.00 },
            { mes: "NOVIEMBRE", key: 11, cantidad: 45, monto: 820000.00 },
            { mes: "DICIEMBRE", key: 12, cantidad: 42, monto: 780000.00 }
          ],
          ventas_por_mes: [
            { mes: "ENERO", key: 1, cantidad: 9, monto: 180000.00 },
            { mes: "FEBRERO", key: 2, cantidad: 12, monto: 240000.00 },
            { mes: "MARZO", key: 3, cantidad: 15, monto: 290000.00 },
            { mes: "ABRIL", key: 4, cantidad: 18, monto: 350000.00 },
            { mes: "MAYO", key: 5, cantidad: 16, monto: 320000.00 },
            { mes: "JUNIO", key: 6, cantidad: 20, monto: 420000.00 },
            { mes: "JULIO", key: 7, cantidad: 19, monto: 380000.00 },
            { mes: "AGOSTO", key: 8, cantidad: 22, monto: 460000.00 },
            { mes: "SEPTIEMBRE", key: 9, cantidad: 18, monto: 425000.00 },
            { mes: "OCTUBRE", key: 10, cantidad: 25, monto: 520000.00 },
            { mes: "NOVIEMBRE", key: 11, cantidad: 28, monto: 580000.00 },
            { mes: "DICIEMBRE", key: 12, cantidad: 30, monto: 620000.00 }
          ],
          top_vendedores: [
            { id: 15, name: "Carlos Mendoza", total_cotizaciones: 8, total_ventas: 5, monto_ventas: 125000.00, conversion_rate: 62.50 },
            { id: 23, name: "Ana García", total_cotizaciones: 12, total_ventas: 7, monto_ventas: 180000.00, conversion_rate: 58.33 },
            { id: 8, name: "Luis Rodríguez", total_cotizaciones: 10, total_ventas: 6, monto_ventas: 150000.00, conversion_rate: 60.00 },
            { id: 31, name: "María López", total_cotizaciones: 15, total_ventas: 8, monto_ventas: 200000.00, conversion_rate: 53.33 },
            { id: 19, name: "Juan Pérez", total_cotizaciones: 9, total_ventas: 4, monto_ventas: 98000.00, conversion_rate: 44.44 }
          ],
          productos_mas_vendidos: [
            { id: 45, nombre: "Letrero LED Premium 2x1m", total_cotizaciones: 12, cantidad_total: 15, cantidad_vendida: 8, monto_ventas: 96000.00 },
            { id: 23, nombre: "Banner Publicitario Vinilo", total_cotizaciones: 18, cantidad_total: 22, cantidad_vendida: 15, monto_ventas: 45000.00 },
            { id: 67, nombre: "Rótulo Acrílico Iluminado", total_cotizaciones: 8, cantidad_total: 10, cantidad_vendida: 6, monto_ventas: 120000.00 },
            { id: 12, nombre: "Impresión Digital Gran Formato", total_cotizaciones: 25, cantidad_total: 35, cantidad_vendida: 28, monto_ventas: 84000.00 },
            { id: 89, nombre: "Letras Corpóreas Acero", total_cotizaciones: 6, cantidad_total: 8, cantidad_vendida: 4, monto_ventas: 160000.00 }
          ],
          estado_produccion: {
            total_productos: 45,
            formatos_impresos: 38,
            formatos_aprobados: 42,
            productos_terminados: 35,
            productos_instalados: 28
          }
        }
      } finally {
        this.loading = false
        emitter.emit("loading", false)
      }
    },
    
    formatNumber(num) {
      return parseInt(num || 0).toLocaleString()
    },
    
    formatCurrency(amount) {
      return parseFloat(amount || 0).toLocaleString('es-MX', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    },
    
    formatPercentage(percent) {
      return parseFloat(percent || 0).toFixed(1)
    }
  }
}
</script>

<style scoped>
.las {
  font-family: 'Line Awesome Free', 'Line Awesome', sans-serif;
}
</style>
